/**
 * Champ de saisie étiqueté.
 *
 * L'étiquette est **toujours visible** (jamais un simple `placeholder`) : un administrateur qui
 * relit un formulaire de contenu doit savoir ce qu'il édite sans deviner. Les consignes de bornes
 * passent par `aide`, qui rappelle la règle du schéma (ex. « nombre entre 0 et 1 »).
 */
import type { ChangeEvent, InputHTMLAttributes } from 'react';

export interface ProprietesChamp extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  readonly id: string;
  readonly etiquette: string;
  /** Consigne permanente (bornes, format), affichée sous le champ. */
  readonly aide?: string;
  /** Message d'erreur, ou `null` s'il n'y en a pas. */
  readonly erreur?: string | null;
}

export function Champ({ id, etiquette, aide, erreur, className, ...reste }: ProprietesChamp) {
  const idAide = aide === undefined ? null : `${id}-aide`;
  const idErreur = erreur === null || erreur === undefined ? null : `${id}-erreur`;
  const decrit = [idAide, idErreur].filter((valeur): valeur is string => valeur !== null);
  const classes = ['champ', className].filter(Boolean).join(' ');
  return (
    <div className={classes}>
      <label className="champ__etiquette" htmlFor={id}>
        {etiquette}
      </label>
      <input
        {...reste}
        id={id}
        className="champ__saisie"
        aria-invalid={idErreur === null ? undefined : true}
        aria-describedby={decrit.length === 0 ? undefined : decrit.join(' ')}
      />
      {idAide === null ? null : (
        <p className="champ__aide" id={idAide}>
          {aide}
        </p>
      )}
      {idErreur === null ? null : (
        <p className="champ__erreur" id={idErreur} role="alert">
          {erreur}
        </p>
      )}
    </div>
  );
}

/** Adaptateur : `onChange` typé sur la valeur texte, pour alléger les formulaires. */
export function lireValeur(evenement: ChangeEvent<HTMLInputElement>): string {
  return evenement.target.value;
}
